/**
 * Sections of the item drawer: the live mission, quality findings with their fixes, links,
 * where the item came from, its history, and the dialogs for a status override and a new link.
 */
import { ArrowUpRight, Bot, ExternalLink, FileText, Hand, History, Link2, ListTodo, Plus, Search, Siren, Square, Unlink, Wand2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ITEM_KIND_LABEL, LINK_LABEL, LINK_TYPES, QUALITY_LABEL, SIGNAL_KIND_LABEL, linksOf, statusLabel } from '@orgus/protocol';
import type { BacklogItem, Id, ItemHistoryEntry, ItemLink, ItemStatus, LinkType, QualityIssue } from '@orgus/protocol';
import { Button, ConfirmDialog, Dialog, ErrorState, IconButton, Input, Pressable, REVEAL_GROUP, RelativeTime, RevealSlot, Select, Skeleton, Textarea, cn, toast } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { stopMission, updateItem } from '../missions/data.ts';
import type { MissionRow } from '../missions/model.ts';
import { ItemState, KindIcon, MissionStatePill, ProgressBar } from '../missions/parts.tsx';
import { addLink, itemHistory, removeLink, undoGroom } from './data.ts';
import { linkTargets } from './model.ts';
import { KeyLabel } from './parts.tsx';

// ---------------------------------------------------------------------------
// Live mission
// ---------------------------------------------------------------------------

/** The mission working the item right now: its state, progress, blockers, and how to reach or stop it. */
export function LiveMissionPanel({ row, onOpenMission }: { row: MissionRow; onOpenMission: (id: Id) => void }) {
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const v = row.view;
  const session = [...v.sessions].reverse().find((s) => s.session)?.session ?? null;
  const blocker = v.blockers[0];
  return (
    <div className={cn('flex flex-col gap-3 rounded-card border p-3', v.state === 'waiting' ? 'border-status-attention-line bg-status-attention-soft' : 'border-border bg-bg')} aria-label="Mission working on this item">
      <div className="flex items-center gap-3">
        <MissionStatePill state={v.state} variant="soft" />
        <Pressable className="min-w-0 flex-1 truncate text-ui font-medium text-fg hover:underline" onClick={() => onOpenMission(row.mission.id)}>
          {row.mission.title}
        </Pressable>
      </div>
      <ProgressBar value={v.progress} label={`Progress of ${row.mission.title}`} caption={v.progressLabel} tone="accent" />
      {blocker && (
        <p className="flex items-start gap-2 text-ui text-fg">
          <Hand className="size-4 shrink-0 translate-y-0.5 text-status-attention" aria-hidden />
          <span className="min-w-0">{blocker.title}</span>
        </p>
      )}
      {session?.lastText && !blocker && <p className="line-clamp-2 text-ui text-fg-muted">{session.lastText}</p>}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" icon={<ArrowUpRight />} onClick={() => onOpenMission(row.mission.id)}>
          Open mission
        </Button>
        {session && (
          <Button variant="ghost" size="sm" icon={<ExternalLink />} onClick={() => navigate(`/sessions?session=${encodeURIComponent(session.id)}`)}>
            Open session
          </Button>
        )}
        <Button variant="ghost" size="sm" icon={<Square />} onClick={() => setConfirm(true)}>
          Stop mission
        </Button>
      </div>
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Stop mission "${row.mission.title}"?`}
        description="Its live sessions stop and the item goes back to ready."
        confirmLabel="Stop mission"
        onConfirm={async () => {
          const r = await stopMission(row.mission.id);
          toast.success('Mission stopped', r.message);
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status: follows the mission, with an override
// ---------------------------------------------------------------------------

export function OverrideDialog({ item, status, open, onOpenChange, missionTitle }: { item: BacklogItem; status: ItemStatus | null; open: boolean; onOpenChange: (o: boolean) => void; missionTitle: string }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setReason('');
      setError(null);
      setTouched(false);
    }
  }, [open]);
  const submit = async () => {
    setTouched(true);
    if (!reason.trim() || !status) return;
    setBusy(true);
    setError(null);
    try {
      await updateItem(item.id, { status, override: { reason: reason.trim() } });
      toast.success(`Status set to ${statusLabel(item.kind, status)}`, 'Missions no longer move it until the next start.');
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Override the status of ${item.key}?`}
      description={`Its status follows mission "${missionTitle}"; an override holds until the next start and is audited.`}
      size="md"
      dismissible={!busy}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Keep following the mission
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            Set to {status ? statusLabel(item.kind, status) : 'status'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Textarea label="Reason" placeholder="Reviewed the branch myself; the mission is still tidying up." rows={2} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} autoFocus error={touched && !reason.trim() ? 'Say why, so the audit log explains it.' : undefined} />
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Quality
// ---------------------------------------------------------------------------

export function QualityPanel({ issues, onFix }: { issues: readonly QualityIssue[]; onFix: (q: QualityIssue) => void }) {
  if (!issues.length) return null;
  return (
    <div className="flex flex-col rounded-card border border-border bg-bg" aria-label="Things to fix">
      <p className="flex h-row items-center gap-2 border-b border-border-subtle px-3 text-ui font-medium text-fg">
        <ListTodo className="size-4 text-fg-subtle" aria-hidden />
        To make it ready
        <span className="text-caption font-normal text-fg-subtle num">{issues.length}</span>
      </p>
      <ul className="flex flex-col divide-y divide-border-subtle">
        {issues.map((q, i) => (
          <li key={`${q.code}-${q.criterionId ?? i}`} className="flex items-center gap-3 px-3 py-2">
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="text-ui text-fg">{QUALITY_LABEL[q.code]}</span>
              <span className="text-caption text-fg-subtle">{q.message}</span>
            </div>
            {q.fix && q.fixLabel && (
              <Button variant="ghost" size="sm" icon={q.fix.kind === 'ai' ? <Wand2 /> : q.fix.kind === 'mark-duplicate' ? <Link2 /> : <Plus />} onClick={() => onFix(q)} className="shrink-0">
                {q.fixLabel}
              </Button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

/** Link choices as people read them, each mapped to a stored type and direction. */
interface LinkChoice {
  value: string;
  label: string;
  type: LinkType;
  inverse: boolean;
}
const LINK_CHOICES: LinkChoice[] = LINK_TYPES.flatMap((t): LinkChoice[] =>
  t === 'relates'
    ? [{ value: 'relates', label: LINK_LABEL.relates.out, type: t, inverse: false }]
    : [
        { value: `${t}:out`, label: LINK_LABEL[t].out, type: t, inverse: false },
        { value: `${t}:in`, label: LINK_LABEL[t].in, type: t, inverse: true },
      ],
);

export function LinksSection({ item, items, links, onOpenItem, preset, onPresetUsed }: { item: BacklogItem; items: readonly BacklogItem[]; links: readonly ItemLink[]; onOpenItem: (id: Id) => void; preset?: string | null; onPresetUsed?: () => void }) {
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const rows = useMemo(() => linksOf(item.id, links), [item.id, links]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  useEffect(() => {
    if (preset) {
      setAdding(preset);
      onPresetUsed?.();
    }
  }, [preset, onPresetUsed]);
  return (
    <div className="flex flex-col gap-1">
      {rows.length ? (
        <ul className="-mx-2 flex flex-col">
          {rows.map((r) => {
            const o = byId.get(r.otherId);
            if (!o) return null;
            return (
              <li key={r.link.id} className={cn(REVEAL_GROUP, 'flex h-row items-center gap-3 rounded-control px-2 hover:bg-hover')}>
                <span className="w-28 shrink-0 truncate text-caption text-fg-subtle">{r.label}</span>
                <Pressable className="flex min-w-0 flex-1 items-center gap-2" onClick={() => onOpenItem(o.id)}>
                  <KindIcon kind={o.kind} tooltip={false} />
                  <KeyLabel value={o.key} />
                  <span className={cn('truncate text-ui', o.status === 'done' || o.status === 'dropped' ? 'text-fg-subtle line-through' : 'text-fg')}>{o.title}</span>
                </Pressable>
                <ItemState status={o.status} kind={o.kind} className="text-caption" />
                <RevealSlot>
                  <IconButton
                    label={`Remove link to ${o.key}`}
                    icon={<Unlink />}
                    size="xs"
                    onClick={async () => {
                      setError(null);
                      try {
                        await removeLink(r.link.id);
                        toast.success('Link removed', `${r.label} ${o.key}`);
                      } catch (e) {
                        setError(toUiError(e));
                      }
                    }}
                  />
                </RevealSlot>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-ui text-fg-subtle">{item.kind === 'defect' ? 'Link what this defect affects, so it shows on that item.' : 'No links. Say what blocks it, what it implements, or what it duplicates.'}</p>
      )}
      {error && <ErrorState variant="inline" error={error} />}
      <div>
        <Button variant="ghost" size="sm" icon={<Plus />} onClick={() => setAdding(item.kind === 'defect' ? 'affects:out' : 'blocks:in')}>
          Add link
        </Button>
      </div>
      <AddLinkDialog item={item} items={items} open={adding !== null} initialChoice={adding ?? 'relates'} onOpenChange={(o) => !o && setAdding(null)} />
    </div>
  );
}

function AddLinkDialog({ item, items, open, initialChoice, onOpenChange }: { item: BacklogItem; items: readonly BacklogItem[]; open: boolean; initialChoice: string; onOpenChange: (o: boolean) => void }) {
  const [choice, setChoice] = useState(initialChoice);
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<Id | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  useEffect(() => {
    if (open) {
      setChoice(initialChoice);
      setQ('');
      setError(null);
    }
  }, [open, initialChoice]);
  const targets = useMemo(() => linkTargets(items, item, q), [items, item, q]);
  const c = LINK_CHOICES.find((x) => x.value === choice) ?? LINK_CHOICES[0]!;
  const link = async (to: BacklogItem) => {
    setBusy(to.id);
    setError(null);
    try {
      await addLink({ type: c.type, from: item.id, to: to.id, inverse: c.inverse });
      toast.success('Linked', `${item.key} ${c.label.toLowerCase()} ${to.key}`);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Link ${item.key}`} size="md">
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <Select label={`${item.key}`} value={choice} onValueChange={setChoice} options={LINK_CHOICES.map((x) => ({ value: x.value, label: x.label }))} wrapperClassName="w-44" />
          <Input aria-label="Find an item" icon={<Search />} placeholder="Key or title" value={q} onChange={(e) => setQ(e.target.value)} autoFocus wrapperClassName="flex-1" />
        </div>
        <ul className="-mx-1 flex max-h-80 flex-col overflow-y-auto" aria-label="Items to link">
          {targets.map((t) => (
            <li key={t.id}>
              <Pressable className="flex h-menu-item items-center gap-2 rounded-control px-2 hover:bg-hover" disabled={!!busy} onClick={() => void link(t)}>
                <KindIcon kind={t.kind} tooltip={false} />
                <KeyLabel value={t.key} className="w-20" />
                <span className="min-w-0 flex-1 truncate text-ui text-fg">{t.title}</span>
                <span className="text-caption text-fg-subtle">{ITEM_KIND_LABEL[t.kind]}</span>
              </Pressable>
            </li>
          ))}
          {!targets.length && <li className="px-2 py-3 text-ui text-fg-subtle">Nothing in this project matches.</li>}
        </ul>
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Source and history
// ---------------------------------------------------------------------------

export function SourceSection({ item, onOpenAttachment }: { item: BacklogItem; onOpenAttachment?: (id: Id) => void }) {
  const s = item.source;
  if (!s) return null;
  const icon = s.kind === 'signal' ? <Siren className="size-4" aria-hidden /> : s.kind === 'ai' ? <Bot className="size-4" aria-hidden /> : <FileText className="size-4" aria-hidden />;
  return (
    <div className="flex items-start gap-2 text-ui text-fg-muted">
      <span className="flex h-5 items-center text-fg-subtle">{icon}</span>
      <div className="flex min-w-0 flex-col">
        <span>
          {s.kind === 'document' ? 'From the document ' : s.kind === 'signal' ? `From a ${s.signal ? SIGNAL_KIND_LABEL[s.signal.kind].toLowerCase() : 'runtime failure'}: ` : 'Written by AI: '}
          {s.attachmentId && onOpenAttachment ? (
            <Pressable className="inline w-auto rounded-control text-fg underline-offset-2 hover:underline" onClick={() => onOpenAttachment(s.attachmentId!)}>
              {s.name}
            </Pressable>
          ) : (
            <span className="text-fg">{s.name}</span>
          )}
        </span>
        {s.section && <span className="text-caption text-fg-subtle">Section {s.section}{s.sections?.length ? ` (${s.sections.join(', ')})` : ''}</span>}
      </div>
    </div>
  );
}

const ACTION_TEXT: Record<string, string> = {
  'item.create': 'created it',
  'item.update': 'changed',
  'item.override': 'overrode the status',
  'item.sync': 'moved it with its mission',
  'ac.check': 'checked a criterion',
  'link.add': 'linked',
  'link.remove': 'removed a link',
  'autopilot.groom': 'groomed it',
  'autopilot.groom.undo': 'undid a grooming change',
};

function show(v: unknown): string {
  if (v == null || v === '') return 'none';
  if (Array.isArray(v)) return v.length ? v.join(', ') : 'none';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  return String(v).replace(/_/g, ' ');
}

/** `open`: load at once (inside a section that is itself a disclosure), with no Show history step. */
export function HistorySection({ item, open: openProp }: { item: BacklogItem; open?: boolean }) {
  const [open, setOpen] = useState(!!openProp);
  const [entries, setEntries] = useState<ItemHistoryEntry[] | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const load = async () => {
    setError(null);
    try {
      setEntries(await itemHistory(item.id));
    } catch (e) {
      setError(toUiError(e));
    }
  };
  useEffect(() => {
    setEntries(null);
    if (open) void load();
    // Reload when another item opens, or this one changes while the history is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, open ? item.updatedAt : 0]);
  if (!open) {
    return (
      <Button variant="ghost" size="sm" icon={<History />} onClick={() => setOpen(true)}>
        Show history
      </Button>
    );
  }
  if (error) return <ErrorState variant="inline" error={error} onRetry={load} />;
  if (!entries) return <Skeleton className="h-16" />;
  if (!entries.length) return <p className="text-ui text-fg-subtle">No changes recorded yet.</p>;
  const undone = new Set(entries.filter((e) => e.action === 'autopilot.groom.undo').map((e) => e.groomId));
  const undo = async (groomId: Id) => {
    try {
      await undoGroom(groomId);
      await load();
    } catch (e) {
      setError(toUiError(e));
    }
  };
  return (
    <ol className="flex flex-col gap-2" aria-label="History, newest first">
      {[...entries].reverse().map((e, i) => (
        <li key={i} className="flex items-start gap-3 text-ui">
          <RelativeTime ts={e.at} className="w-20 shrink-0 text-caption leading-5 text-fg-subtle" />
          <span className="min-w-0 text-fg-muted">
            <span className="text-fg">{e.by}</span> {ACTION_TEXT[e.action] ?? e.action}
            {e.field && e.action === 'item.update' && (
              <>
                {' '}
                {e.field}
                {e.from !== undefined || e.to !== undefined ? (
                  <>
                    {' '}
                    from <span className="text-fg">{show(e.from)}</span> to <span className="text-fg">{show(e.to)}</span>
                  </>
                ) : null}
              </>
            )}
            {(e.action === 'item.sync' || e.action === 'item.override') && e.to !== undefined && (
              <>
                {' '}
                to <span className="text-fg">{show(e.to)}</span>
              </>
            )}
            {(e.action === 'link.add' || e.action === 'link.remove') && (
              <>
                {' '}
                {e.field?.toLowerCase()} <span className="text-fg">{show(e.to ?? e.from)}</span>
              </>
            )}
            {e.note && <span className="block text-caption text-fg-subtle">{e.note}</span>}
          </span>
          {e.action === 'autopilot.groom' && e.groomId && !undone.has(e.groomId) && (
            <Button variant="ghost" size="sm" className="ml-auto shrink-0" onClick={() => void undo(e.groomId!)}>
              Undo
            </Button>
          )}
        </li>
      ))}
    </ol>
  );
}


